import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import {
  BarChart,
  Bar,
  AreaChart,
  Area,
  LineChart,
  Line,
  ComposedChart,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";
import {
  MapContainer,
  TileLayer,
  Polyline,
  Marker,
  Popup,
  Tooltip as LeafletTooltip,
  useMap,
  useMapEvents,
} from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import DatePicker from "react-multi-date-picker";
import persian from "react-date-object/calendars/persian";
import persian_fa from "react-date-object/locales/persian_fa";
import useDashboardLogStore from "../stores/dashboardLogStore";
import "./DeviceAnalyticsPage.css";
import PrintReport from "./PrintReport";
import RouteLayer from "./RouteLayer";

/* ── Duration options ────────────────────────────────────────────────────── */
const DURATIONS = [
  { value: "day", label: "امروز" },
  { value: "week", label: "این هفته" },
  { value: "month", label: "این ماه" },
  // { value: "year",  label: "این سال" },
  { value: "ytd", label: "از ابتدای سال" },
];

/* ── Map settings ────────────────────────────────────────────────────────── */
const MAP_CLUSTER_PIXELS = 44;
const MAP_MAX_ZOOM = 21;
const MAP_CLUSTER_FIT_PADDING = 60;

/* ── Print report helpers ────────────────────────────────────────────────── */
// Format helpers — everything is shown in Persian digits for the print copy.
const pNum = (v, digits = 2) =>
  v == null
    ? "—"
    : Number(v).toLocaleString("fa-IR", {
        minimumFractionDigits: digits,
        maximumFractionDigits: digits,
      });

const pInt = (v) => (v == null ? "—" : Number(v).toLocaleString("fa-IR"));

// Jalali date for the report's header.
const todayFa = () =>
  new Date().toLocaleDateString("fa-IR", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });

// Latest (last non-null) value for a given key.
const latestOf = (buckets, key) => {
  for (let i = buckets.length - 1; i >= 0; i -= 1) {
    if (buckets[i][key] != null) {
      return { value: buckets[i][key], title: buckets[i].title };
    }
  }
  return null;
};

/* ── Satellite tile providers ────────────────────────────────────────────── */
const SATELLITE_PROVIDERS = [
  {
    name: "esri-proxied",
    url: "/esri-proxy/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
    attribution:
      "Tiles &copy; Esri &mdash; Source: Esri, Maxar, Earthstar Geographics",
    maxNativeZoom: 17,
  },
  {
    name: "eox-s2cloudless-2020",
    url: "https://tiles.maps.eox.at/wmts/1.0.0/s2cloudless-2020_3857/default/g/{z}/{y}/{x}.jpg",
    attribution:
      'Sentinel-2 cloudless by <a href="https://s2maps.eu">EOX</a> (contains modified Copernicus Sentinel data)',
    maxNativeZoom: 14,
  },
  {
    name: "eox-s2cloudless-2018",
    url: "https://tiles.maps.eox.at/wmts/1.0.0/s2cloudless-2018_3857/default/g/{z}/{y}/{x}.jpg",
    attribution:
      'Sentinel-2 cloudless by <a href="https://s2maps.eu">EOX</a> (contains modified Copernicus Sentinel data)',
    maxNativeZoom: 14,
  },
];

function SatelliteTileLayer() {
  const [providerIndex, setProviderIndex] = useState(0);
  const errorCountRef = useRef(0);

  const provider = SATELLITE_PROVIDERS[providerIndex];

  const handleTileError = useCallback(() => {
    errorCountRef.current += 1;
    if (
      errorCountRef.current >= 2 &&
      providerIndex < SATELLITE_PROVIDERS.length - 1
    ) {
      errorCountRef.current = 0;
      setProviderIndex((i) => i + 1);
    }
  }, [providerIndex]);

  useEffect(() => {
    errorCountRef.current = 0;
  }, [providerIndex]);

  return (
    <TileLayer
      key={`satellite-${provider.name}`}
      url={provider.url}
      attribution={provider.attribution}
      maxZoom={21}
      maxNativeZoom={provider.maxNativeZoom}
      eventHandlers={{ tileerror: handleTileError }}
    />
  );
}

/* ── Speed Gauge (value in km/h) ─────────────────────────────────────────── */
function SpeedGauge({ value }) {
  const max = 60;
  const pct = value != null ? Math.min(Math.max(value, 0) / max, 1) : 0;

  const cx = 100,
    cy = 98,
    r = 82;
  const x1 = cx - r,
    y1 = cy;
  const x2 = cx + r,
    y2 = cy;

  const bgPath = `M ${x1} ${y1} A ${r} ${r} 0 0 1 ${x2} ${y2}`;

  const theta = (1 - pct) * Math.PI;
  const xv = cx + r * Math.cos(theta);
  const yv = cy - r * Math.sin(theta);
  const valuePath =
    value != null && pct > 0
      ? `M ${x1} ${y1} A ${r} ${r} 0 0 1 ${xv.toFixed(2)} ${yv.toFixed(2)}`
      : null;

  const color = pct >= 0.66 ? "#3fb950" : pct >= 0.33 ? "#f0a03a" : "#f85149";

  return (
    <svg viewBox="0 0 200 118" className="dap-gauge-svg">
      <path
        d={bgPath}
        fill="none"
        stroke="#21262d"
        strokeWidth="13"
        strokeLinecap="round"
      />
      {valuePath && (
        <path
          d={valuePath}
          fill="none"
          stroke={color}
          strokeWidth="13"
          strokeLinecap="round"
        />
      )}
      <text
        x="100"
        y="90"
        textAnchor="middle"
        fill="#e6edf3"
        fontSize="24"
        fontWeight="700"
        fontFamily="inherit"
      >
        {value != null ? value.toFixed(1).toLocaleString("fa-IR") : "—"}
      </text>
      <text
        x="100"
        y="108"
        textAnchor="middle"
        fill="#7d8590"
        fontSize="10"
        fontFamily="inherit"
      >
        کیلومتر بر ساعت
      </text>
      <text
        x={x1}
        y={y1 + 16}
        textAnchor="middle"
        fill="#484f58"
        fontSize="9"
        fontFamily="inherit"
      >
        ۰
      </text>
      <text
        x={x2}
        y={y2 + 16}
        textAnchor="middle"
        fill="#484f58"
        fontSize="9"
        fontFamily="inherit"
      >
        {max.toLocaleString("fa-IR")}
      </text>
    </svg>
  );
}

/* ── Cell Signal (value is negative dBm) ─────────────────────────────────── */
function getCellSignalInfo(value) {
  if (value == null || Number.isNaN(Number(value))) {
    return { bars: null, color: "#484f58", quality: "—" };
  }

  const dbm = Number(value);

  if (dbm >= -60) return { bars: 7, color: "#3fb950", quality: "عالی" };
  if (dbm >= -70) return { bars: 6, color: "#3fb950", quality: "خوب" };
  if (dbm >= -80)
    return { bars: 5, color: "#3fb950", quality: "متوسط رو به خوب" };
  if (dbm >= -90) return { bars: 4, color: "#f0a03a", quality: "متوسط" };
  if (dbm >= -100) return { bars: 3, color: "#f0a03a", quality: "ضعیف" };
  if (dbm >= -110) return { bars: 2, color: "#f85149", quality: "خیلی ضعیف" };
  return { bars: 1, color: "#f85149", quality: "بسیار ضعیف" };
}

function CellSignalWidget({ value }) {
  const { bars, color, quality } = getCellSignalInfo(value);

  return (
    <div className="dap-widget dap-signal-widget">
      <div className="dap-widget-icon-row">
        <i className="bi bi-reception-4 dap-widget-icon" style={{ color }} />
        <span className="dap-widget-label">کیفیت سیگنال</span>
      </div>

      <div className="dap-signal-bars">
        {[1, 2, 3, 4, 5, 6, 7].map((b) => (
          <div
            key={b}
            className="dap-signal-bar"
            style={{
              height: `${b * 14.28}%`,
              background: bars != null && b <= bars ? color : "#21262d",
            }}
          />
        ))}
      </div>

      {/* <div className="dap-widget-value" style={{ color }}>
        {value != null ? `${Number(value).toFixed(1)} dBm` : "—"}
      </div> */}

      <div
        className="dap-widget-label"
        style={{ color, marginTop: "4px", fontSize: "12px" }}
      >
        {quality}
      </div>
    </div>
  );
}

/* ── Temperature ─────────────────────────────────────────────────────────── */
function TempWidget({ value }) {
  const color =
    value == null
      ? "#484f58"
      : value > 90
      ? "#f85149"
      : value > 70
      ? "#f0a03a"
      : "#58a6ff";
  return (
    <div className="dap-widget dap-temp-widget">
      <div className="dap-widget-icon-row">
        <i
          className="bi bi-thermometer-half dap-widget-icon"
          style={{ color }}
        />
        <span className="dap-widget-label">دما</span>
      </div>
      <div className="dap-widget-value" style={{ color }}>
        {value != null ? `${value.toFixed(1)}°C` : "—"}
      </div>
    </div>
  );
}

/* ── Stat card ───────────────────────────────────────────────────────────── */
function StatCard({ icon, label, value, unit, color = "#3fb950" }) {
  return (
    <div className="dap-stat-card">
      <i className={`bi ${icon} dap-stat-icon`} style={{ color }} />
      <div className="dap-stat-body">
        <span className="dap-stat-value">
          {value != null ? value : "—"}
          {value != null && unit && (
            <span className="dap-stat-unit"> {unit}</span>
          )}
        </span>
        <span className="dap-stat-label">{label}</span>
      </div>
    </div>
  );
}

/* ── Recharts custom tooltip ─────────────────────────────────────────────── */
function ChartTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="dap-tooltip">
      <p className="dap-tooltip-label">{label}</p>
      {payload.map(
        (p, i) =>
          p.value != null && (
            <p key={i} className="dap-tooltip-row" style={{ color: p.color }}>
              {p.name}:{" "}
              {typeof p.value === "number" ? p.value.toFixed(2) : p.value}
            </p>
          )
      )}
    </div>
  );
}

/* ── Average / Sum helpers ───────────────────────────────────────────────── */
const avgOf = (buckets, key) => {
  const vals = buckets.map((b) => b[key]).filter((v) => v != null);
  return vals.length ? vals.reduce((a, b) => a + b, 0) / vals.length : null;
};
const sumOf = (buckets, key) => {
  const vals = buckets.map((b) => b[key]).filter((v) => v != null);
  return vals.length ? vals.reduce((a, b) => a + b, 0) : null;
};

/* ── Map helpers ─────────────────────────────────────────────────────────── */
const escapeHtml = (value) =>
  String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

const createPointIcon = (number) =>
  L.divIcon({
    className: "dap-map-marker-wrapper",
    html: `<div class="dap-map-marker${
      number >= 100 ? " dap-map-marker-wide" : ""
    }">${number}</div>`,
    iconSize: [30, 30],
    iconAnchor: [15, 15],
    popupAnchor: [0, -16],
  });

const createClusterIcon = (count) =>
  L.divIcon({
    className: "dap-map-marker-wrapper",
    html: `<div class="dap-map-marker dap-map-cluster${
      count >= 100 ? " dap-map-marker-wide" : ""
    }">${count}</div>`,
    iconSize: [40, 40],
    iconAnchor: [20, 20],
    popupAnchor: [0, -20],
  });

function MapController({ expanded, onExpand, positions, pointsKey }) {
  const map = useMap();
  const positionsRef = useRef(positions);
  positionsRef.current = positions;

  useMapEvents({
    click: () => {
      if (!expanded) onExpand();
    },
  });

  const fitToPoints = useCallback(
    (animate) => {
      const pts = positionsRef.current;
      if (!pts.length) return;
      map.fitBounds(L.latLngBounds(pts), {
        padding: [40, 40],
        maxZoom: 16,
        animate,
      });
    },
    [map]
  );

  useEffect(() => {
    fitToPoints(false);
  }, [pointsKey, fitToPoints]);

  useEffect(() => {
    if (expanded) map.scrollWheelZoom.enable();
    else map.scrollWheelZoom.disable();

    const t = setTimeout(() => {
      map.invalidateSize();
      fitToPoints(false);
    }, 60);
    return () => clearTimeout(t);
  }, [expanded, map, fitToPoints]);

  return null;
}

function ClusteredMarkers({ points }) {
  const map = useMap();
  const [zoom, setZoom] = useState(() => map.getZoom());

  useMapEvents({
    zoomend: () => setZoom(map.getZoom()),
  });

  const clusters = useMemo(() => {
    const result = [];

    points.forEach((point) => {
      const px = map.project([point.lat, point.lon], zoom);

      let target = null;
      for (const candidate of result) {
        if (px.distanceTo(candidate.px) <= MAP_CLUSTER_PIXELS) {
          target = candidate;
          break;
        }
      }

      if (target) {
        target.points.push(point);
        target.sumX += px.x;
        target.sumY += px.y;
        target.px = L.point(
          target.sumX / target.points.length,
          target.sumY / target.points.length
        );
      } else {
        result.push({ px, sumX: px.x, sumY: px.y, points: [point] });
      }
    });

    return result.map((c) => {
      const center = map.unproject(c.px, zoom);
      const orders = c.points.map((p) => p.order);
      return {
        key: `${c.points[0].order}-${c.points.length}`,
        points: c.points,
        center,
        minOrder: Math.min(...orders),
        maxOrder: Math.max(...orders),
        icon:
          c.points.length > 1
            ? createClusterIcon(c.points.length)
            : createPointIcon(c.points[0].order),
      };
    });
  }, [points, zoom, map]);

  const openClusterList = (cluster) => {
    const rows = cluster.points
      .map(
        (p) => `
          <div style="padding:6px 0;border-bottom:1px solid #e5e5e5">
            <strong>#${p.order}</strong>${
          p.title ? ` — ${escapeHtml(p.title)}` : ""
        }<br/>
            <span dir="ltr">${p.lat.toFixed(6)}, ${p.lon.toFixed(6)}</span>
          </div>`
      )
      .join("");

    L.popup({ maxHeight: 260, minWidth: 200 })
      .setLatLng(cluster.center)
      .setContent(
        `<div dir="rtl"><strong>${cluster.points.length.toLocaleString(
          "fa-IR"
        )} نقطه در یک مکان</strong>${rows}</div>`
      )
      .openOn(map);
  };

  const handleClusterClick = (cluster) => {
    const bounds = L.latLngBounds(cluster.points.map((p) => [p.lat, p.lon]));

    const spreadAtMaxZoom = map
      .project(bounds.getSouthWest(), MAP_MAX_ZOOM)
      .distanceTo(map.project(bounds.getNorthEast(), MAP_MAX_ZOOM));

    const targetZoom = Math.min(
      map.getBoundsZoom(
        bounds,
        false,
        L.point(MAP_CLUSTER_FIT_PADDING, MAP_CLUSTER_FIT_PADDING)
      ),
      MAP_MAX_ZOOM
    );

    if (spreadAtMaxZoom > MAP_CLUSTER_PIXELS && targetZoom > map.getZoom()) {
      map.fitBounds(bounds, {
        padding: [MAP_CLUSTER_FIT_PADDING, MAP_CLUSTER_FIT_PADDING],
        maxZoom: MAP_MAX_ZOOM,
      });
    } else {
      openClusterList(cluster);
    }
  };

  return (
    <>
      {clusters.map((cluster) => {
        if (cluster.points.length === 1) {
          const point = cluster.points[0];

          return (
            <Marker
              key={`map-point-${point.order}`}
              position={[point.lat, point.lon]}
              icon={cluster.icon}
            >
              <LeafletTooltip direction="top" offset={[0, -14]}>
                <div dir="ltr" style={{ textAlign: "left" }}>
                  <strong>#{point.order}</strong>
                  <br />
                  Lat: {point.lat.toFixed(6)}
                  <br />
                  Lon: {point.lon.toFixed(6)}
                </div>
              </LeafletTooltip>

              <Popup>
                <div dir="rtl" style={{ minWidth: 150 }}>
                  <strong>نقطه #{point.order}</strong>
                  <br />
                  عرض جغرافیایی: <span dir="ltr">{point.lat.toFixed(6)}</span>
                  <br />
                  طول جغرافیایی: <span dir="ltr">{point.lon.toFixed(6)}</span>
                  {point.title && (
                    <>
                      <br />
                      {point.title}
                    </>
                  )}
                </div>
              </Popup>
            </Marker>
          );
        }

        return (
          <Marker
            key={`map-cluster-${cluster.key}`}
            position={[cluster.center.lat, cluster.center.lng]}
            icon={cluster.icon}
            eventHandlers={{ click: () => handleClusterClick(cluster) }}
          >
            <LeafletTooltip direction="top" offset={[0, -20]}>
              <div dir="rtl">
                {cluster.points.length.toLocaleString("fa-IR")} نقطه (
                <span dir="ltr">
                  #{cluster.minOrder} – #{cluster.maxOrder}
                </span>
                )
                <br />
                برای مشاهده کلیک کنید
              </div>
            </LeafletTooltip>
          </Marker>
        );
      })}
    </>
  );
}

/* ── Page ────────────────────────────────────────────────────────────────── */
export default function DeviceAnalyticsPage() {
  const { deviceId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { getDeviceAnalytics } = useDashboardLogStore();

  const { deviceSerial, machineName, userName } = location.state ?? {};

  const [duration, setDuration] = useState("day");
  const [pickedDate, setPickedDate] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [mapExpanded, setMapExpanded] = useState(false);
  const [mapLayer, setMapLayer] = useState("street");
  const [fuelChartExpanded, setFuelChartExpanded] = useState(false);

  const datePickerRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const date =
      duration === "date" && pickedDate
        ? {
            jy: pickedDate.year,
            jm: pickedDate.month.number,
            jd: pickedDate.day,
          }
        : undefined;
    const result = await getDeviceAnalytics(deviceId, duration, date);
    setLoading(false);
    if (result.success) {
      setData(result.data);
    } else {
      setError(result.error || "خطا در بارگذاری اطلاعات دستگاه");
    }
  }, [deviceId, duration, pickedDate, getDeviceAnalytics]);

  useEffect(() => {
    load();
  }, [load]);

  /** Open the browser print dialog. The hidden report is the only thing
   *  that will actually render on paper (see @media print in the CSS). */
  const handlePrint = useCallback(() => {
    window.print();
  }, []);

  const buckets = useMemo(() => data?.buckets ?? [], [data]);
  const hasFuel = buckets.some((b) => b.fuel_usage != null);
  const hasSpeed = buckets.some((b) => b.speed != null);
  const hasDist = buckets.some((b) => b.distance != null);
  const hasPerform = buckets.some((b) => b.perform_time != null);
  const hasTemp = buckets.some((b) => b.temp != null);
  const hasSignal = buckets.some((b) => b.cell_signal != null);

  const avgSignal = hasSignal ? avgOf(buckets, "cell_signal") : null;
  const avgTemp = hasTemp ? avgOf(buckets, "temp") : null;

  // Latest (last non-null) values — used for the print report.
  const latestSignal = useMemo(
    () => latestOf(buckets, "cell_signal"),
    [buckets]
  );
  const latestSpeed = useMemo(() => latestOf(buckets, "speed"), [buckets]);
  const latestTemp = useMemo(() => latestOf(buckets, "temp"), [buckets]);

  const totalPerformSec = hasPerform ? sumOf(buckets, "perform_time") : null;
  const totalPerformMin = totalPerformSec != null ? totalPerformSec / 60 : null;

  const totalFuel = hasFuel ? sumOf(buckets, "fuel_usage") : null;
  const totalDist = hasDist ? sumOf(buckets, "distance") : null;
  const avgSpeed = hasSpeed ? avgOf(buckets, "speed") : null;
  const totalLogs = buckets.reduce((s, b) => s + (b.log_count ?? 0), 0);

  const online = data?.online ?? null;

  const bucketsWithMin = useMemo(
    () =>
      buckets.map((b) => ({
        ...b,
        perform_time_min: b.perform_time != null ? b.perform_time / 60 : null,
      })),
    [buckets]
  );

  const showCombinedChart = fuelChartExpanded || !hasFuel;
  const hasAnyFuelSectionData = hasFuel || hasDist || hasPerform;

  const mapPoints = useMemo(
    () =>
      buckets
        .map((b) => ({
          ...b,
          lat: Number(b.lat),
          lon: Number(b.lon),
        }))
        .filter(
          (p) =>
            Number.isFinite(p.lat) &&
            Number.isFinite(p.lon) &&
            p.lat !== 0 &&
            p.lon !== 0 &&
            p.lat >= -90 &&
            p.lat <= 90 &&
            p.lon >= -180 &&
            p.lon <= 180
        )
        .map((p, index) => ({ ...p, order: index + 1 })),
    [buckets]
  );

  const positions = useMemo(
    () => mapPoints.map((p) => [p.lat, p.lon]),
    [mapPoints]
  );
  const pointsKey = useMemo(
    () => mapPoints.map((p) => `${p.lat},${p.lon}`).join("|"),
    [mapPoints]
  );
  const hasMap = mapPoints.length > 0;

  const mapCenter = hasMap
    ? [
        positions.reduce((s, p) => s + p[0], 0) / positions.length,
        positions.reduce((s, p) => s + p[1], 0) / positions.length,
      ]
    : [34.0954, 49.7013];

  useEffect(() => {
    if (!mapExpanded) return undefined;

    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const onKeyDown = (e) => {
      if (e.key === "Escape") setMapExpanded(false);
    };
    window.addEventListener("keydown", onKeyDown);

    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [mapExpanded]);

  useEffect(() => {
    if (!hasMap) setMapExpanded(false);
  }, [hasMap]);

  const handleCustomDayClick = () => {
    if (pickedDate) {
      setDuration("date");
    } else {
      datePickerRef.current?.openCalendar();
    }
  };

  const handleDateChange = (dateObject) => {
    if (!dateObject) return;
    setPickedDate(dateObject);
    setDuration("date");
  };

  return (
    <>
      <div className="dap-page">
        {/* ── Back + header ── */}
        <div className="dap-header">
          <button className="dap-back-btn" onClick={() => navigate(-1)}>
            <i className="bi bi-arrow-right" />
          </button>
          <div className="dap-header-text">
            <h1 className="dap-title">
              <i className="bi bi-cpu dap-title-icon" />
              آنالیز دستگاه
              {deviceSerial && (
                <span className="dap-title-serial ltr"> — {deviceSerial}</span>
              )}
            </h1>
            {(machineName || userName) && (
              <p className="dap-subtitle">
                {machineName && <span>{machineName}</span>}
                {machineName && userName && (
                  <span className="dap-subtitle-sep"> · </span>
                )}
                {userName && <span>{userName}</span>}
              </p>
            )}
            {(data?.period_label || online != null) && (
              <p className="dap-period">
                {data?.period_label && <span>{data.period_label}</span>}
                {online != null && (
                  <span
                    className={`dap-online-badge${
                      online ? " online" : " offline"
                    }`}
                  >
                    <span className="dap-online-dot" />
                    {online ? "آنلاین" : "آفلاین"}
                  </span>
                )}
              </p>
            )}
          </div>

          {!loading && !error && data && (
            <button
              type="button"
              className="dap-print-btn"
              onClick={handlePrint}
              title="چاپ یا ذخیره به صورت PDF"
              aria-label="چاپ گزارش"
            >
              <i className="bi bi-printer" />
              <span>چاپ</span>
            </button>
          )}
        </div>

        {/* ── Duration tabs ── */}
        <div className="dap-duration-tabs">
          {DURATIONS.map((d) => (
            <button
              key={d.value}
              className={`dap-duration-tab${
                duration === d.value ? " active" : ""
              }`}
              onClick={() => setDuration(d.value)}
              disabled={loading}
            >
              {d.label}
            </button>
          ))}

          <DatePicker
            ref={datePickerRef}
            value={pickedDate}
            onChange={handleDateChange}
            calendar={persian}
            locale={persian_fa}
            calendarPosition="bottom-right"
            render={(value, openCalendar) => (
              <button
                type="button"
                className={`dap-duration-tab${
                  duration === "date" ? " active" : ""
                }`}
                onClick={() => {
                  handleCustomDayClick();
                  openCalendar();
                }}
                disabled={loading}
              >
                {pickedDate ? pickedDate.format("DD MMMM") : "روز دلخواه"}
              </button>
            )}
          />
        </div>

        {/* ── Loading ── */}
        {loading && (
          <div className="dap-loading">
            <span className="dap-spinner" />
            در حال بارگذاری...
          </div>
        )}

        {/* ── Error ── */}
        {error && !loading && (
          <div className="dap-error">
            <i className="bi bi-exclamation-triangle-fill" />
            <span>{error}</span>
            <button className="dap-retry-btn" onClick={load}>
              تلاش مجدد
            </button>
          </div>
        )}

        {!loading && !error && data && (
          <>
            {/* ── Summary stat cards ── */}
            <div className="dap-stats-row">
              <StatCard
                icon="bi-list-ol"
                label="تعداد لاگ‌ها"
                value={totalLogs.toLocaleString("fa-IR")}
                color="#7d8590"
              />
              {hasFuel && (
                <StatCard
                  icon="bi-fuel-pump"
                  label="مجموع مصرف سوخت"
                  value={totalFuel != null ? totalFuel.toFixed(2) : null}
                  unit="لیتر"
                  color="#3fb950"
                />
              )}
              {hasDist && (
                <StatCard
                  icon="bi-sign-turn-right"
                  label="مجموع مسافت"
                  value={totalDist != null ? totalDist.toFixed(2) : null}
                  unit="km"
                  color="#58a6ff"
                />
              )}
              {hasPerform && totalPerformMin != null && (
                <StatCard
                  icon="bi-clock-history"
                  label="مجموع زمان کارکرد"
                  value={totalPerformMin.toFixed(1).toLocaleString("fa-IR")}
                  unit="دقیقه"
                  color="#a371f7"
                />
              )}
            </div>

            {/* ── Fuel / Combined chart (toggled) ── */}
            {hasAnyFuelSectionData && (
              <div className="dap-chart-section">
                <div className="dap-section-header">
                  <h2 className="dap-section-title">
                    <i
                      className={`bi ${
                        showCombinedChart ? "bi-bar-chart-line" : "bi-fuel-pump"
                      }`}
                    />
                    {showCombinedChart
                      ? "مسافت، زمان کارکرد و مصرف سوخت"
                      : "مصرف سوخت"}
                  </h2>
                  {hasFuel && (
                    <button
                      type="button"
                      className={`dap-chart-toggle${
                        fuelChartExpanded ? " active" : ""
                      }`}
                      onClick={() => setFuelChartExpanded((v) => !v)}
                      title={fuelChartExpanded ? "نمایش کمتر" : "جزئیات بیشتر"}
                      aria-label={
                        fuelChartExpanded ? "نمایش کمتر" : "جزئیات بیشتر"
                      }
                    >
                      <i
                        className={`bi ${
                          fuelChartExpanded
                            ? "bi-arrows-collapse"
                            : "bi-arrows-expand"
                        }`}
                      />
                      <span>
                        {fuelChartExpanded ? "نمایش کمتر" : "جزئیات بیشتر"}
                      </span>
                    </button>
                  )}
                </div>

                {showCombinedChart ? (
                  <ResponsiveContainer width="100%" height={240}>
                    <ComposedChart
                      data={bucketsWithMin}
                      margin={{ top: 5, right: 10, left: 0, bottom: 5 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#21262d" />
                      <XAxis
                        dataKey="title"
                        tick={{
                          fill: "#7d8590",
                          fontSize: 11,
                          fontFamily: "inherit",
                        }}
                      />
                      <YAxis
                        yAxisId="left"
                        orientation="left"
                        tick={{ fill: "#7d8590", fontSize: 11 }}
                        width={40}
                      />
                      <YAxis
                        yAxisId="right"
                        orientation="right"
                        tick={{ fill: "#58a6ff", fontSize: 11 }}
                        width={40}
                      />
                      <Tooltip content={<ChartTooltip />} />
                      <Legend
                        wrapperStyle={{ fontSize: "12px", color: "#7d8590" }}
                      />
                      {hasFuel && (
                        <Line
                          yAxisId="left"
                          type="monotone"
                          dataKey="fuel_usage"
                          name="سوخت (لیتر)"
                          stroke="#3fb950"
                          strokeWidth={2}
                          dot={false}
                        />
                      )}
                      {hasPerform && (
                        <Line
                          yAxisId="left"
                          type="monotone"
                          dataKey="perform_time_min"
                          name="زمان کارکرد (دقیقه)"
                          stroke="#a371f7"
                          strokeWidth={2}
                          dot={false}
                        />
                      )}
                      {hasDist && (
                        <Line
                          yAxisId="right"
                          type="monotone"
                          dataKey="distance"
                          name="مسافت (km)"
                          stroke="#58a6ff"
                          strokeWidth={2}
                          dot={false}
                        />
                      )}
                    </ComposedChart>
                  </ResponsiveContainer>
                ) : (
                  <ResponsiveContainer width="100%" height={220}>
                    <BarChart
                      data={buckets}
                      margin={{ top: 5, right: 10, left: 0, bottom: 5 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#21262d" />
                      <XAxis
                        dataKey="title"
                        tick={{
                          fill: "#7d8590",
                          fontSize: 11,
                          fontFamily: "inherit",
                        }}
                      />
                      <YAxis
                        tick={{ fill: "#7d8590", fontSize: 11 }}
                        width={40}
                      />
                      <Tooltip content={<ChartTooltip />} />
                      <Bar
                        dataKey="fuel_usage"
                        name="سوخت (لیتر)"
                        fill="#3fb950"
                        radius={[4, 4, 0, 0]}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </div>
            )}

            {/* ── Speed / Signal / Temp widgets ── */}
            {(hasSpeed || hasSignal || hasTemp) && (
              <div className="dap-widgets-section">
                {hasSpeed && (
                  <div className="dap-widget-card">
                    <h2 className="dap-section-title">
                      <i className="bi bi-speedometer2" />
                      میانگین سرعت
                    </h2>
                    <div className="dap-gauge-wrap">
                      <SpeedGauge value={avgSpeed} />
                    </div>
                  </div>
                )}
                {hasSignal && (
                  <div className="dap-widget-card">
                    <h2 className="dap-section-title">
                      <i className="bi bi-reception-4" />
                      سیگنال سلولی
                    </h2>
                    <CellSignalWidget value={avgSignal} />
                  </div>
                )}
                {hasTemp && (
                  <div className="dap-widget-card">
                    <h2 className="dap-section-title">
                      <i className="bi bi-thermometer-half" />
                      میانگین دمای موتور
                    </h2>
                    <TempWidget value={avgTemp} />
                  </div>
                )}
              </div>
            )}

            {/* ── Perform Time chart ── */}
            {hasPerform &&
              buckets.filter((b) => b.perform_time != null).length > 1 && (
                <div className="dap-chart-section">
                  <h2 className="dap-section-title">
                    <i className="bi bi-clock-history" />
                    روند زمان کارکرد
                  </h2>
                  <ResponsiveContainer width="100%" height={200}>
                    <BarChart
                      data={buckets}
                      margin={{ top: 5, right: 10, left: 0, bottom: 5 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#21262d" />
                      <XAxis
                        dataKey="title"
                        tick={{
                          fill: "#7d8590",
                          fontSize: 11,
                          fontFamily: "inherit",
                        }}
                      />
                      <YAxis
                        tick={{ fill: "#7d8590", fontSize: 11 }}
                        width={40}
                        domain={[0, 60]}
                      />
                      <Tooltip
                        content={({ active, payload, label }) => {
                          if (!active || !payload?.length) return null;
                          const sec = payload[0].value;
                          return (
                            <div className="dap-tooltip">
                              <p className="dap-tooltip-label">{label}</p>
                              <p
                                className="dap-tooltip-row"
                                style={{ color: "#a371f7" }}
                              >
                                زمان کارکرد:{" "}
                                {sec != null ? (sec / 60).toFixed(1) : "—"}{" "}
                                دقیقه
                              </p>
                            </div>
                          );
                        }}
                      />
                      <Bar
                        dataKey="perform_time"
                        name="زمان کارکرد"
                        fill="#a371f7"
                        radius={[4, 4, 0, 0]}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}

            {/* ── Speed & Distance chart ── */}
            {(hasSpeed || hasDist) && (
              <div className="dap-chart-section">
                <h2 className="dap-section-title">
                  <i className="bi bi-speedometer2" />
                  سرعت و مسافت
                </h2>
                <ResponsiveContainer width="100%" height={220}>
                  <LineChart
                    data={buckets}
                    margin={{ top: 5, right: 30, left: 0, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#21262d" />
                    <XAxis
                      dataKey="title"
                      tick={{
                        fill: "#7d8590",
                        fontSize: 11,
                        fontFamily: "inherit",
                      }}
                    />
                    {hasSpeed && (
                      <YAxis
                        yAxisId="speed"
                        orientation="right"
                        tick={{ fill: "#f0a03a", fontSize: 11 }}
                        width={40}
                      />
                    )}
                    {hasDist && (
                      <YAxis
                        yAxisId="distance"
                        orientation="left"
                        tick={{ fill: "#58a6ff", fontSize: 11 }}
                        width={40}
                      />
                    )}
                    <Tooltip content={<ChartTooltip />} />
                    <Legend
                      wrapperStyle={{ fontSize: "12px", color: "#7d8590" }}
                    />
                    {hasSpeed && (
                      <Line
                        yAxisId="speed"
                        type="monotone"
                        dataKey="speed"
                        name="سرعت (km/h)"
                        stroke="#f0a03a"
                        strokeWidth={2}
                        dot={false}
                      />
                    )}
                    {hasDist && (
                      <Line
                        yAxisId="distance"
                        type="monotone"
                        dataKey="distance"
                        name="مسافت (km)"
                        stroke="#58a6ff"
                        strokeWidth={2}
                        dot={false}
                      />
                    )}
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}

            {/* ── Temperature chart ── */}
            {hasTemp && buckets.filter((b) => b.temp != null).length > 1 && (
              <div className="dap-chart-section">
                <h2 className="dap-section-title">
                  <i className="bi bi-thermometer-half" />
                  روند دما
                </h2>
                <ResponsiveContainer width="100%" height={180}>
                  <AreaChart
                    data={buckets}
                    margin={{ top: 5, right: 10, left: 0, bottom: 5 }}
                  >
                    <defs>
                      <linearGradient id="tempGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop
                          offset="5%"
                          stopColor="#58a6ff"
                          stopOpacity={0.3}
                        />
                        <stop
                          offset="95%"
                          stopColor="#58a6ff"
                          stopOpacity={0.02}
                        />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#21262d" />
                    <XAxis
                      dataKey="title"
                      tick={{
                        fill: "#7d8590",
                        fontSize: 11,
                        fontFamily: "inherit",
                      }}
                    />
                    <YAxis
                      tick={{ fill: "#7d8590", fontSize: 11 }}
                      width={40}
                    />
                    <Tooltip content={<ChartTooltip />} />
                    <Area
                      type="monotone"
                      dataKey="temp"
                      name="دما (°C)"
                      stroke="#58a6ff"
                      fill="url(#tempGrad)"
                      strokeWidth={2}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            )}

            {/* ── Location map ── */}
            {hasMap && (
              <div className="dap-chart-section">
                <h2 className="dap-section-title">
                  <i className="bi bi-geo-alt-fill" />
                  مسیر دستگاه
                </h2>

                <div
                  className={`dap-map-wrap${mapExpanded ? " expanded" : ""}`}
                >
                  <div className="dap-map-controls">
                    <button
                      type="button"
                      className={`dap-map-toggle${
                        mapLayer === "satellite" ? " active" : ""
                      }`}
                      onClick={() =>
                        setMapLayer((v) =>
                          v === "satellite" ? "street" : "satellite"
                        )
                      }
                      title={
                        mapLayer === "satellite"
                          ? "نمایش نقشه معمولی"
                          : "نمایش ماهواره‌ای"
                      }
                      aria-label={
                        mapLayer === "satellite"
                          ? "نمایش نقشه معمولی"
                          : "نمایش ماهواره‌ای"
                      }
                    >
                      <i
                        className={`bi ${
                          mapLayer === "satellite"
                            ? "bi-map"
                            : "bi-globe-americas"
                        }`}
                      />
                    </button>

                    <button
                      type="button"
                      className="dap-map-toggle"
                      onClick={() => setMapExpanded((v) => !v)}
                      title={mapExpanded ? "بستن نقشه" : "نمایش تمام‌صفحه"}
                      aria-label={mapExpanded ? "بستن نقشه" : "نمایش تمام‌صفحه"}
                    >
                      <i
                        className={`bi ${
                          mapExpanded ? "bi-x-lg" : "bi-arrows-fullscreen"
                        }`}
                      />
                    </button>
                  </div>

                  {!mapExpanded && (
                    <div className="dap-map-hint">
                      برای بزرگ‌نمایی روی نقشه کلیک کنید
                    </div>
                  )}

                  <MapContainer
                    center={mapCenter}
                    zoom={10}
                    maxZoom={MAP_MAX_ZOOM}
                    className="dap-map"
                    scrollWheelZoom={false}
                  >
                    {mapLayer === "satellite" ? (
                      <SatelliteTileLayer />
                    ) : (
                      <TileLayer
                        key="street"
                        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                        maxZoom={21}
                        maxNativeZoom={19}
                      />
                    )}

                    <MapController
                      expanded={mapExpanded}
                      onExpand={() => setMapExpanded(true)}
                      positions={positions}
                      pointsKey={pointsKey}
                    />

                    <RouteLayer positions={positions} />

                    <ClusteredMarkers points={mapPoints} />
                  </MapContainer>
                </div>
              </div>
            )}

            {/* ── No data at all ── */}
            {buckets.every((b) => b.log_count === 0) && (
              <div className="dap-empty">
                <i className="bi bi-inbox dap-empty-icon" />
                <p>در بازه زمانی انتخابی لاگی وجود ندارد.</p>
              </div>
            )}
          </>
        )}
      </div>

      {/* ── Print report (only visible on paper) ── */}
      {!loading && !error && data && (
        <PrintReport
          deviceSerial={deviceSerial}
          machineName={machineName}
          userName={userName}
          data={data}
          buckets={buckets}
          mapPoints={mapPoints}
          duration={duration}
        />
      )}
    </>
  );
}
